import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi, LoginPayload } from '../api/auth.api';
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
} from '../api/http';

export interface AuthContextValue {
  isBootstrapping: boolean;
  isAuthenticated: boolean;
  userId: number | null;
  companyId: number | null;
  email: string | null;
  companyName: string | null;
  companySlug: string | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [userId, setUserId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companySlug, setCompanySlug] = useState<string | null>(null);

  const reset = useCallback(() => {
    clearStoredAccessToken();
    setUserId(null);
    setCompanyId(null);
    setEmail(null);
    setCompanyName(null);
    setCompanySlug(null);
  }, []);

  const logout = useCallback(() => {
    reset();
  }, [reset]);

  const login = useCallback(async (payload: LoginPayload) => {
    const result = await authApi.login(payload);
    setStoredAccessToken(result.accessToken);
    setUserId(result.userId);
    setCompanyId(result.companyId);
    setEmail(result.email);
    setCompanyName(result.companyName);
    setCompanySlug(result.companySlug);
  }, []);

  useEffect(() => {
    void (async () => {
      const token = getStoredAccessToken();
      if (!token) {
        setIsBootstrapping(false);
        return;
      }
      try {
        const me = await authApi.me();
        setUserId(me.userId);
        setCompanyId(me.companyId);
        setEmail(me.email);
        setCompanyName(me.companyName);
        setCompanySlug(me.companySlug);
      } catch {
        reset();
      } finally {
        setIsBootstrapping(false);
      }
    })();
  }, [reset]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isBootstrapping,
      isAuthenticated: userId !== null && companySlug !== null,
      userId,
      companyId,
      email,
      companyName,
      companySlug,
      login,
      logout,
    }),
    [
      isBootstrapping,
      userId,
      companyId,
      email,
      companyName,
      companySlug,
      login,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return ctx;
}
